/**
 * Runtime override for network-dependent values (mainnet vs testnet).
 *
 * In development, the user can flip between mainnet and testnet from the
 * `DevDebugHeader` without restarting the dev server. The choice is persisted
 * in localStorage and re-read at module load on the next page reload.
 *
 * Production builds and non-dev environments always fall back to the regular
 * `process.env.*` values, so this file is a no-op there.
 */

export type RuntimeNetworkId = 'mainnet' | 'testnet';

export interface NetworkContracts {
  PERIODICAL_STAKE: string;
  WHITELISTED_STAKE: string;
  VIP_STAKE: string;
  WHITELISTED_STAKE_POOL_ID: number;
}

export interface NetworkNativeCurrency {
  name: string;
  symbol: string;
  decimals: number;
}

export interface NetworkConfig {
  id: RuntimeNetworkId;
  label: string;
  chainIdHex: string;
  networkId: string;
  rpcUrl: string;
  chainName: string;
  blockExplorerUrl: string;
  nativeCurrency: NetworkNativeCurrency;
  contracts: NetworkContracts;
}

/**
 * Hardcoded fallbacks used when the env file does not provide an explicit
 * REACT_APP_*_MAINNET / REACT_APP_*_TESTNET override.
 *
 * Prefer setting these in your dev `.env` file so addresses can be updated
 * without a code change.
 */
const MAINNET_DEFAULTS = {
  rpcUrl: 'https://polygon.drpc.org',
  contracts: {
    PERIODICAL_STAKE: '0x29cE6711fA6A8196D2b9538C5cE6293941e98749',
    WHITELISTED_STAKE: '0x9f6fF1B0B9120Dc25c0b7234068c75A791D62F30',
    VIP_STAKE: '0xa816fC819c2BD73c0AEdf60E0b06daF2Bff9691F',
    WHITELISTED_STAKE_POOL_ID: 0,
  },
} as const;

const TESTNET_DEFAULTS = {
  rpcUrl: 'https://rpc-amoy.polygon.technology/',
  contracts: {
    PERIODICAL_STAKE: '0x413f20DE64D103f6f010cbF45446764941d0703D',
    WHITELISTED_STAKE: '0x2301cbf2682CD825B4a7b0b6d6c4E757Cb8A6113',
    VIP_STAKE: '0x0d8ab209583050A9959322229A7c314c66e40E6f',
    WHITELISTED_STAKE_POOL_ID: 0,
  },
} as const;

const pickEnv = (...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = process.env[key];
    if (value && value.trim()) return value.trim();
  }
  return undefined;
};

export const MAINNET_CONFIG: NetworkConfig = {
  id: 'mainnet',
  label: 'Mainnet',
  chainIdHex: '0x89',
  networkId: '137',
  rpcUrl: pickEnv('REACT_APP_RPC_URL_MAINNET') ?? MAINNET_DEFAULTS.rpcUrl,
  chainName: 'Polygon Mainnet',
  blockExplorerUrl: 'https://polygonscan.com/',
  nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
  contracts: {
    PERIODICAL_STAKE:
      pickEnv('REACT_APP_PERIODICAL_STAKE_ADDRESS_MAINNET') ?? MAINNET_DEFAULTS.contracts.PERIODICAL_STAKE,
    WHITELISTED_STAKE:
      pickEnv('REACT_APP_WHITELISTED_STAKE_ADDRESS_MAINNET') ?? MAINNET_DEFAULTS.contracts.WHITELISTED_STAKE,
    VIP_STAKE: pickEnv('REACT_APP_VIP_STAKE_ADDRESS_MAINNET') ?? MAINNET_DEFAULTS.contracts.VIP_STAKE,
    WHITELISTED_STAKE_POOL_ID:
      Number(pickEnv('REACT_APP_WHITELISTED_STAKE_POOL_ID_MAINNET')) ||
      MAINNET_DEFAULTS.contracts.WHITELISTED_STAKE_POOL_ID,
  },
};

export const TESTNET_CONFIG: NetworkConfig = {
  id: 'testnet',
  label: 'Testnet',
  chainIdHex: '0x13882',
  networkId: '80002',
  rpcUrl: pickEnv('REACT_APP_RPC_URL_TESTNET') ?? TESTNET_DEFAULTS.rpcUrl,
  chainName: 'Polygon Amoy Testnet',
  blockExplorerUrl: 'https://amoy.polygonscan.com/',
  nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
  contracts: {
    PERIODICAL_STAKE:
      pickEnv('REACT_APP_PERIODICAL_STAKE_ADDRESS_TESTNET') ?? TESTNET_DEFAULTS.contracts.PERIODICAL_STAKE,
    WHITELISTED_STAKE:
      pickEnv('REACT_APP_WHITELISTED_STAKE_ADDRESS_TESTNET') ?? TESTNET_DEFAULTS.contracts.WHITELISTED_STAKE,
    VIP_STAKE: pickEnv('REACT_APP_VIP_STAKE_ADDRESS_TESTNET') ?? TESTNET_DEFAULTS.contracts.VIP_STAKE,
    WHITELISTED_STAKE_POOL_ID:
      Number(pickEnv('REACT_APP_WHITELISTED_STAKE_POOL_ID_TESTNET')) ||
      TESTNET_DEFAULTS.contracts.WHITELISTED_STAKE_POOL_ID,
  },
};

export const NETWORK_OVERRIDE_STORAGE_KEY = 'dev:networkOverride';
const IS_DEV = process.env.NODE_ENV === 'development';

const readStoredOverride = (): RuntimeNetworkId | null => {
  if (!IS_DEV || typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(NETWORK_OVERRIDE_STORAGE_KEY);
    if (value === 'mainnet' || value === 'testnet') return value;
  } catch {
    // localStorage can throw (e.g. SSR, private mode). Ignore.
  }
  return null;
};

/**
 * Returns the active dev network override, or null when no override is set.
 * When null, callers should use their normal `process.env.*` source.
 */
export const getActiveNetworkOverride = (): NetworkConfig | null => {
  const override = readStoredOverride();
  if (override === 'mainnet') return MAINNET_CONFIG;
  if (override === 'testnet') return TESTNET_CONFIG;
  return null;
};

export const getStoredNetworkOverride = (): RuntimeNetworkId | null => readStoredOverride();

export const setStoredNetworkOverride = (id: RuntimeNetworkId): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NETWORK_OVERRIDE_STORAGE_KEY, id);
  } catch {
    /* noop */
  }
};

export const clearStoredNetworkOverride = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(NETWORK_OVERRIDE_STORAGE_KEY);
  } catch {
    /* noop */
  }
};
